/**
 * InviteNotificationBadge
 *
 * Shows a badge with pending invite count for the logged-in user.
 * When clicked, expands an inline dropdown listing all pending invites
 * with Accept / Decline actions.
 *
 * Usage:
 *   <InviteNotificationBadge />
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { Badge, Spin, message as antMessage } from 'antd';
import { BellOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import { getMyInvites, acceptInvite, declineInvite } from '../../api/challenges';

export default function InviteNotificationBadge() {
  const { user, isLoggedIn } = useAuth();
  const [invites, setInvites] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(null); // inviteId being acted on
  const containerRef = useRef(null);

  const pending = invites.filter((inv) => inv.status === 'pending');

  // ── Fetch pending invites ────────────────────────────────────────────────
  const fetchInvites = useCallback(async () => {
    if (!isLoggedIn) return;
    setLoading(true);
    try {
      const data = await getMyInvites(user);
      setInvites(data);
    } catch (_) {
      // silent
    } finally {
      setLoading(false);
    }
  }, [user, isLoggedIn]);

  useEffect(() => {
    fetchInvites();
  }, [fetchInvites]);

  // Close on outside click
  useEffect(() => {
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  if (!isLoggedIn) return null;

  // ── Actions ───────────────────────────────────────────────────────────────
  async function handleAccept(inviteId) {
    setActing(inviteId);
    try {
      await acceptInvite(user, inviteId);
      antMessage.success('Đã tham gia team!');
      await fetchInvites();
    } catch (err) {
      const code = err?.response?.data?.error?.details?.code;
      const msg =
        code === 'TEAM_FULL' ? 'Team đã đầy!'
        : code === 'TEAM_LOCKED' ? 'Campaign đã bắt đầu, không thể tham gia team!'
        : err?.response?.data?.error?.message || 'Không thể chấp nhận lời mời.';
      antMessage.error(msg);
    } finally {
      setActing(null);
    }
  }

  async function handleDecline(inviteId) {
    setActing(inviteId);
    try {
      await declineInvite(user, inviteId);
      antMessage.info('Đã từ chối lời mời.');
      await fetchInvites();
    } catch (_) {
      antMessage.error('Không thể từ chối lời mời.');
    } finally {
      setActing(null);
    }
  }

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative', display: 'inline-block' }}
    >
      {/* Bell icon with badge */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Lời mời team"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '4px 8px',
          fontSize: 20,
          display: 'flex',
          alignItems: 'center',
          color: 'inherit',
        }}
      >
        <Badge count={pending.length} size="small" offset={[2, -2]}>
          <BellOutlined style={{ fontSize: 20 }} />
        </Badge>
      </button>

      {/* Dropdown */}
      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            width: 340,
            background: '#fff',
            borderRadius: 8,
            boxShadow: '0 6px 24px rgba(0,0,0,0.15)',
            zIndex: 1000,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '10px 16px',
              borderBottom: '1px solid #f0f0f0',
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            Lời mời tham gia team
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 24 }}>
              <Spin />
            </div>
          ) : invites.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: '#8c8c8c', fontSize: 13 }}>
              Không có lời mời nào.
            </div>
          ) : (
            <div style={{ maxHeight: 360, overflowY: 'auto' }}>
              {invites.map((inv) => {
                const isPending = inv.status === 'pending';
                const isExpired = new Date(inv.expires_at) < new Date();
                const isActing = acting === inv.id;

                return (
                  <div
                    key={inv.id}
                    style={{
                      padding: '12px 16px',
                      borderBottom: '1px solid #f5f5f5',
                      opacity: !isPending || isExpired ? 0.6 : 1,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      {inv.team?.logo_url && (
                        <img
                          src={inv.team.logo_url}
                          alt=""
                          style={{ width: 28, height: 28, borderRadius: 4, objectFit: 'cover' }}
                        />
                      )}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{inv.team?.name}</div>
                        <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                          {inv.campaign?.name}
                          {' · '}
                          Mời bởi {inv.invited_by?.fullname || 'Đội trưởng'}
                        </div>
                      </div>
                      {/* Status badge for non-pending */}
                      {!isPending && (
                        <span
                          style={{
                            fontSize: 11,
                            padding: '2px 8px',
                            borderRadius: 10,
                            background: inv.status === 'accepted' ? '#f6ffed' : '#fff1f0',
                            color: inv.status === 'accepted' ? '#52c41a' : '#cf1322',
                          }}
                        >
                          {inv.status === 'accepted' ? 'Đã chấp nhận' : 'Đã từ chối'}
                        </span>
                      )}
                    </div>

                    {isPending && !isExpired && (
                      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                        <button
                          onClick={() => handleAccept(inv.id)}
                          disabled={isActing}
                          style={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 4,
                            padding: '5px 0',
                            border: 'none',
                            borderRadius: 6,
                            background: '#1677ff',
                            color: '#fff',
                            cursor: isActing ? 'not-allowed' : 'pointer',
                            fontSize: 13,
                          }}
                        >
                          <CheckOutlined /> Chấp nhận
                        </button>
                        <button
                          onClick={() => handleDecline(inv.id)}
                          disabled={isActing}
                          style={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 4,
                            padding: '5px 0',
                            border: '1px solid #d9d9d9',
                            borderRadius: 6,
                            background: '#fff',
                            color: '#595959',
                            cursor: isActing ? 'not-allowed' : 'pointer',
                            fontSize: 13,
                          }}
                        >
                          <CloseOutlined /> Từ chối
                        </button>
                      </div>
                    )}

                    {isPending && isExpired && (
                      <div style={{ fontSize: 12, color: '#cf1322', marginTop: 4 }}>
                        Lời mời đã hết hạn
                      </div>
                    )}

                    <div style={{ fontSize: 11, color: '#bfbfbf', marginTop: 4 }}>
                      Hết hạn: {new Date(inv.expires_at).toLocaleDateString('vi-VN')}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
